
import React, { useState } from 'react';
import { DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle } from 'lucide-react';
import { EnrichmentDialogState } from './enrichmentTypes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter as ConfirmationDialogFooter, // Alias to avoid conflict
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface EnrichmentDialogFooterProps {
  dialogState: EnrichmentDialogState;
  onClose: () => void;
  onApplyChanges: () => void;
  isApplyingChanges: boolean;
  canApplyChanges: boolean;
  onCancelEnrichment?: (runId: string, clientId: string) => Promise<void>;
  isCancellingEnrichment?: boolean;
  currentRunId?: string | null;
  clientId?: string;
}

export const EnrichmentDialogFooter: React.FC<EnrichmentDialogFooterProps> = ({
  dialogState,
  onClose,
  onApplyChanges,
  isApplyingChanges,
  canApplyChanges,
  onCancelEnrichment,
  isCancellingEnrichment,
  currentRunId,
  clientId,
}) => {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const handleConfirmCancel = async () => {
    if (onCancelEnrichment && currentRunId && clientId) {
      await onCancelEnrichment(currentRunId, clientId);
    }
    setShowCancelConfirm(false);
  };

  // Valid states where an enrichment process can be cancelled by the user
  const cancellableStates: EnrichmentDialogState[] = [
    'loading',
    'queued',
    'requested',
    'initiated_lutra',
    'processing',
    'pending_lutra_data',
    'polling_for_results',
    'extracting',
    'finalizing',
    'processing_retrieved_data'
  ];

  const isCancellable =
    cancellableStates.includes(dialogState) &&
    !!onCancelEnrichment &&
    !!currentRunId &&
    !!clientId;

  if (dialogState === 'success') {
    return (
      <DialogFooter className="pt-4 border-t">
        <DialogClose asChild>
          <Button variant="outline" onClick={onClose} disabled={isApplyingChanges}>Cancel</Button>
        </DialogClose>
        <Button onClick={onApplyChanges} disabled={isApplyingChanges || !canApplyChanges}>
          {isApplyingChanges && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Apply Selected Changes
        </Button>
      </DialogFooter>
    );
  } else {
    // Footer for all non-SUCCESS states
    return (
      <DialogFooter className="pt-4 border-t">
        {isCancellable && (
          <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
            <AlertDialogTrigger asChild>
              <Button 
                variant="outline"
                className="text-destructive border-destructive hover:bg-destructive/10 hover:text-destructive focus-visible:ring-destructive/50"
                disabled={isCancellingEnrichment}
              >
                {isCancellingEnrichment ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <AlertTriangle className="mr-2 h-4 w-4" />
                )}
                Cancel Enrichment
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action will attempt to cancel the ongoing enrichment process.
                  It might not be possible to stop it immediately if it's too far along.
                  Cancelled enrichments cannot be resumed.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <ConfirmationDialogFooter>
                <AlertDialogCancel disabled={isCancellingEnrichment}>Keep Enriching</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleConfirmCancel}
                  disabled={isCancellingEnrichment}
                  className="bg-destructive hover:bg-destructive/90"
                >
                  {isCancellingEnrichment && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Yes, Cancel Enrichment
                </AlertDialogAction>
              </ConfirmationDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        <DialogClose asChild>
          <Button variant="outline" onClick={onClose} disabled={isCancellingEnrichment || isApplyingChanges}>
            Close
          </Button>
        </DialogClose>
      </DialogFooter>
    );
  }
};
